import { Fragment, useEffect, useState } from "react";
import { ThumbsUp } from "lucide-react";
import StarRow from "@/components/StarRow";
import { formatReviewAge } from "@/lib/reviewAge";
import type { Review } from "@/data/lpReviews";

/**
 * A review from the Google Business Profile, in the card the profile itself
 * shows it in.
 *
 * Rendered as markup rather than pasted in as screenshots on purpose. A
 * screenshot of a review is a few hundred kilobytes that blurs on a retina
 * phone, cannot be read by a screen reader or by Google, does not reflow at
 * 360px, and goes stale silently the moment a review is edited or removed.
 * The same card in HTML is a few hundred bytes, stays sharp, and its text is
 * the review itself.
 *
 * The reviews are copied verbatim into `src/data/lpReviews.ts`; nothing here
 * rewrites, trims or reorders what a reviewer wrote.
 */

/** Attribution shown on every card, straight from the Business Profile. */
const SERVICE = "Professional Trading Course";
const CATEGORY = "Education";

/** The Google mark, for attribution — this is where the review is from. */
export const GoogleGlyph = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 48 48" className={className} role="img" aria-label="Google">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/** The scalloped seal the source shows beside the rating. Decorative. */
const VerifiedBadge = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden focusable="false">
    <path
      fill="#1a73e8"
      d="M12 0Q14.74 1.76 18 1.61Q19.5 4.5 22.39 6Q22.24 9.26 24 12Q22.24 14.74 22.39 18Q19.5 19.5 18 22.39Q14.74 22.24 12 24Q9.26 22.24 6 22.39Q4.5 19.5 1.61 18Q1.76 14.74 0 12Q1.76 9.26 1.61 6Q4.5 4.5 6 1.61Q9.26 1.76 12 0Z"
    />
    <path
      d="M7.4 12.3l3.1 3.1 6.1-6.4"
      fill="none"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * The colour behind a reviewer's initial.
 *
 * Taken from the exported artwork rather than computed, for the four
 * reviewers it shows an initial for: Andrew's circle is purple there and
 * Jake's is brown, and a hash of the name had them the other way round. For
 * those four the card is now the artwork exactly.
 *
 * The other four have photographs in the export, which are not in this
 * repository — see src/assets/reviews/original/README.md. Their tones echo
 * the dominant colour of the photograph they stand in for, so the wall reads
 * as the same set of people until the files arrive.
 */
const AVATAR_TONE: Record<string, string> = {
  // Shown with an initial in the export; these are its colours.
  "Andrew Johnson": "bg-[#6d3f9e]",
  "Jake Thompson": "bg-[#4a3728]",
  "Robert Smith": "bg-[#4d5b66]",
  "Michael Brown": "bg-[#3d4852]",
  // Shown with a photograph; these stand in for one.
  "Amanda Lewis": "bg-[#5a6b7a]",
  "Michael Rogers": "bg-[#5c4033]",
  "Daniel Cohen": "bg-[#4a5f4a]",
  "Sophie Levy": "bg-[#7a5236]",
};

/** For a reviewer nobody has picked a colour for yet. */
const FALLBACK_TONES = ["bg-[#4b3832]", "bg-[#4a5a68]", "bg-[#3d5a4c]", "bg-[#5a4a6a]", "bg-[#6a4a3a]"];

/**
 * The same reviewer always gets the same colour, however the array is later
 * reordered — a card that changes colour between two pages of the same funnel
 * reads as a different person.
 */
const toneFor = (name: string) =>
  AVATAR_TONE[name] ??
  FALLBACK_TONES[[...name].reduce((sum, c) => sum + c.charCodeAt(0), 0) % FALLBACK_TONES.length];

/**
 * Two size steps, chosen by the card's own width rather than the window's.
 *
 * Every one of these used to be a `sm:` class, which is a query about the
 * viewport — and the generator draws a 380px card inside a 1400px window, so
 * the narrow rendering came out wearing the wide card's type: a truncated
 * "Andrew Jo…" and a ruled footer far too long for the line it sat on.
 */
const SCALE = {
  wide: {
    padding: "p-8",
    avatar: "h-[68px] w-[68px] text-3xl",
    name: "text-2xl",
    meta: "text-lg",
    glyph: "h-8 w-8",
    star: 22,
    badge: "h-7 w-7",
    body: "text-[17px] leading-[1.9]",
    gap: "gap-5",
  },
  compact: {
    padding: "p-6",
    avatar: "h-14 w-14 text-xl",
    name: "text-lg",
    meta: "text-sm",
    glyph: "h-6 w-6",
    star: 18,
    badge: "h-[22px] w-[22px]",
    body: "text-[15px] leading-[1.8]",
    gap: "gap-4",
  },
} as const;

const ReviewAvatar = ({ review, size }: { review: Review; size: string }) =>
  review.avatar ? (
    <img
      src={review.avatar}
      alt=""
      width={136}
      height={136}
      loading="lazy"
      decoding="async"
      className={`shrink-0 rounded-full object-cover ${size}`}
    />
  ) : (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-white ${size} ${toneFor(review.name)}`}
    >
      {review.name.trim().charAt(0).toUpperCase()}
    </span>
  );

export const GoogleReviewCard = ({
  review,
  compact = false,
}: {
  review: Review;
  /**
   * Draw the card at the smaller of the two size steps. Set by whoever knows
   * how wide the card actually is; see `SCALE` for why the stylesheet cannot
   * work it out on its own.
   */
  compact?: boolean;
}) => {
  const age = formatReviewAge(review.date);
  const z = compact ? SCALE.compact : SCALE.wide;

  return (
    <figure className={`flex h-full flex-col rounded-3xl border border-border/50 bg-white shadow-sm ${z.padding}`}>
      <div className={`flex items-start ${z.gap}`}>
        <ReviewAvatar review={review} size={z.avatar} />
        <div className="min-w-0 flex-1">
          <div className={`truncate font-bold text-primary ${z.name}`}>{review.name}</div>
          {(age || review.role) && (
            <div className={`mt-0.5 truncate text-muted-foreground ${z.meta}`}>
              {[review.role, age].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        <GoogleGlyph className={`shrink-0 ${z.glyph}`} />
      </div>

      <div className={`mt-6 flex items-center ${compact ? "gap-3" : "gap-4"}`}>
        <StarRow rating={review.rating} size={z.star} className="gap-1.5" />
        <VerifiedBadge className={z.badge} />
      </div>

      {/* Centred in the space between header and footer, so a short review
          sits in the middle of its card rather than stranded at the top with
          the void below it. The reviewer's own line breaks are kept, and the
          line spacing is the source's own — airy, not the site's usual. */}
      <div className="flex flex-1 items-center py-6">
        <blockquote className={`whitespace-pre-line text-foreground ${z.body}`}>
          {review.text}
        </blockquote>
      </div>

      {/* Ruled, as the source card is — but only on a card wide enough to hold
          the row on one line. A rule left at the end of a wrapped line reads
          as a rendering fault, so the compact step drops them and lets
          spacing carry the grouping instead. */}
      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border/60 pt-5 text-[13px]">
        {/* Plain text, not a button: this is a card on a landing page, and a
            thumbs-up that looks pressable but does nothing is worse than none. */}
        <span className="inline-flex items-center gap-2 font-semibold text-foreground">
          <ThumbsUp className="h-4 w-4" aria-hidden />
          Helpful
        </span>
        {!compact && <span aria-hidden className="h-4 w-px bg-border" />}
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground">Service:</span> {SERVICE}
        </span>
        {!compact && <span aria-hidden className="h-4 w-px bg-border" />}
        <span className="text-muted-foreground">
          <span className="font-semibold text-foreground">Type:</span> {CATEGORY}
        </span>
      </figcaption>
    </figure>
  );
};

/**
 * True while the visitor has asked their system for less motion.
 *
 * Read in JS rather than left to the stylesheet because the answer changes
 * the markup, not just the animation: a stopped marquee would otherwise still
 * be showing the duplicate copy the seamless loop needs, i.e. every review
 * twice in a row.
 */
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!query) return;
    setReduced(query.matches);
    const onChange = () => setReduced(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  return reduced;
};

/**
 * The narrowest card that can hold "Helpful | Service: … | Type: …" on one
 * line. Below it the row wraps and a rule is left at the end of a line,
 * which reads as a rendering fault — so below it the rules are dropped and
 * spacing carries the grouping instead.
 *
 * Neither rendering clears it any more. The source artwork is 1500px wide
 * and could; the cards are drawn at 520 and 380 because that is where they
 * are shown, and a picture does not reflow when it is scaled.
 */
const RULED_FOOTER_MIN_WIDTH = 640;

/** Seconds per lap. Longer lists take longer, so the speed stays constant. */
const secondsPerLap = (count: number) => Math.max(45, count * 8);

/**
 * The size every review image is rendered at, by
 * `scripts/render-review-cards.mjs`. Declared on the `<img>` so the row
 * reserves the right box before a single file has loaded — eight images
 * arriving into an unsized row would shove the page around underneath the
 * visitor.
 */
export const REVIEW_IMAGE = { width: 1400, height: 934 } as const;

/**
 * The narrow rendering, for phones. A landscape card scaled to a 340px screen
 * sets the review at about eight pixels, so the same card is photographed
 * again at a phone's width, where it comes out portrait and legible.
 */
export const REVIEW_IMAGE_NARROW = { width: 760, height: 1018 } as const;

/**
 * One review, as the picture of a card rather than the card itself.
 *
 * The alt text carries the whole review, because that is the review: an
 * image of it is invisible to a screen reader and to a search engine, and
 * the point of putting reviews on a page is that they can be read.
 */
/**
 * Smaller copies of each review picture (src/assets/reviews/sized/), so a
 * 340px phone card fetches a 360 or 600px file instead of the 760px one.
 * Keyed "<reviewer-slug>-<variant>-<width>".
 */
const SIZED = import.meta.glob<string>("@/assets/reviews/sized/*.webp", {
  eager: true,
  import: "default",
  query: "?url",
});
const sized = (name: string, key: string) => {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const hit = Object.entries(SIZED).find(([path]) => path.endsWith(`/${slug}-${key}.webp`));
  return hit?.[1];
};
/** srcset from the sized copies plus the full file; the full file alone when none exist. */
const srcSetFor = (name: string, full: string, fullWidth: number, keys: [string, number][]) => {
  const parts = keys
    .map(([key, w]) => {
      const url = sized(name, key);
      return url ? `${url} ${w}w` : null;
    })
    .filter(Boolean);
  return parts.length ? [...parts, `${full} ${fullWidth}w`].join(", ") : undefined;
};

const ReviewImage = ({ review }: { review: Review }) => (
  <picture>
    {/* The phone-shaped rendering, chosen by the browser before it fetches
        anything, so a phone never downloads the desktop one. */}
    {review.imageNarrow && (
      <source
        media="(max-width: 639px)"
        srcSet={
          srcSetFor(review.name, review.imageNarrow, REVIEW_IMAGE_NARROW.width, [
            ["narrow-360", 360],
            ["narrow-600", 600],
          ]) ?? review.imageNarrow
        }
        sizes="340px"
        width={REVIEW_IMAGE_NARROW.width}
        height={REVIEW_IMAGE_NARROW.height}
      />
    )}
    <img
      src={review.image}
      srcSet={srcSetFor(review.name, review.image, REVIEW_IMAGE.width, [
        ["520", 520],
        ["1040", 1040],
      ])}
      sizes="(min-width: 1024px) 600px, 520px"
      /* One flat line: alt is a single attribute, and the reviewer's line
         breaks mean nothing once it is read aloud. */
      alt={`${review.name}, ${review.rating} out of 5 on Google: ${review.text.replace(/\s+/g, " ").trim()}`}
      width={REVIEW_IMAGE.width}
      height={REVIEW_IMAGE.height}
      loading="lazy"
      decoding="async"
      className="block h-auto w-full rounded-3xl"
    />
  </picture>
);

/**
 * The reviews as a row that scrolls sideways, on a loop with no end.
 *
 * A landing page has one job, and a wall of eight reviews is a wall the
 * visitor has to scroll past to reach the form. The same eight in a moving
 * row take one screen instead of four, and read as a feed that keeps going
 * rather than a list that has been counted out.
 *
 * The row carries the reviews twice: the animation translates the track by
 * half its width, which puts copy two exactly where copy one began, so the
 * lap repeats without a seam. Only the first copy is exposed to assistive
 * technology — the second is scenery, and a screen reader that read all
 * sixteen would be reporting eight reviews that do not exist.
 *
 * Renders nothing when there are no reviews — the same rule the video
 * testimonials follow, so a heading never sits over blank space.
 */
export const GoogleReviewMarquee = ({
  reviews,
  className = "",
  asMarkup = false,
  markupWidth = 520,
}: {
  reviews: Review[];
  className?: string;
  /**
   * Draw the cards rather than their images. This is how the images get
   * made: `scripts/render-review-cards.mjs` loads /lp with `?cards=markup`
   * and photographs the real component, so a picture of a review can never
   * drift from the card it is supposed to be a picture of.
   */
  asMarkup?: boolean;
  /** Card width for `asMarkup`, so the generator can shoot both renderings. */
  markupWidth?: number;
}) => {
  const reduced = usePrefersReducedMotion();

  if (!reviews.length) return null;

  // No duplicate when the row is not moving: there is nothing to loop, and
  // the copy would just be the same eight reviews listed again.
  const copies = reduced ? 1 : 2;

  return (
    <div
      data-testid="lp-review-marquee"
      className={`review-marquee ${reduced ? "overflow-x-auto" : "overflow-hidden"} ${className}`}
    >
      <div
        className="review-marquee-track"
        style={{ "--review-marquee-duration": `${secondsPerLap(reviews.length)}s` } as React.CSSProperties}
      >
        {Array.from({ length: copies }, (_, copy) => (
          <Fragment key={copy}>
            {reviews.map((review, i) => (
              <div
                key={`${copy}-${review.name}-${review.date ?? i}`}
                // The trailing margin belongs to the card rather than the
                // track: see the note on .review-marquee-track in index.css.
                className={
                  asMarkup
                    ? // What the generator photographs, at the width it is
                      // told to: the card's own design size, never a scaled
                      // copy of a different one.
                      "mr-6 shrink-0"
                    : // The exported card is landscape and does not reflow:
                      // at 400px its review sets at about nine pixels, so it
                      // is shown at the width its own type stays readable at.
                      // The phone gets the portrait rendering instead.
                      "mr-4 w-[340px] shrink-0 sm:mr-6 sm:w-[520px] lg:w-[600px]"
                }
                style={asMarkup ? { width: markupWidth } : undefined}
                aria-hidden={copy > 0 || undefined}
              >
                {review.image && !asMarkup ? (
                  <ReviewImage review={review} />
                ) : (
                  <GoogleReviewCard review={review} compact={markupWidth < RULED_FOOTER_MIN_WIDTH} />
                )}
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default GoogleReviewCard;
